import TournamentFooter from '@/components/tournaments/tournament-footer';
import { clubApi } from '@repo/api-client';
import { LoaderFunctionArgs, Outlet, redirect, useLoaderData } from 'react-router';
import { urlTournament } from '@/urls';
import { route as pendingRoute } from './pending';
import { route as openInscriptionsRoute } from './open-inscriptions';
import { route as activeRoute } from './active';
import { route as finishedRoute } from './finished';
import { route as configurePhasesRoute } from './configuring-datetime-phases';
import { DateFormat, formatDate } from '@/data/format/format-date';
import StatusBadge from '@/components/tournaments/status-badge';
import { Portal } from '@repo/ui/components/portal';
import { TournamentDetailsModal } from '@/components/tournaments/tournament-details-modal';

function Header({ tournament }: { tournament: Tournament }) {
  const tournamentMonthFrom = formatDate(tournament?.startDate, DateFormat.MM);
  const tournamentMonthTo = formatDate(tournament?.endDate, DateFormat.MM);
  const tournamentFrom = formatDate(tournament?.startDate, DateFormat.DD);
  const tournamentTo = formatDate(tournament?.endDate, DateFormat.DD);
  const sameMonth = tournamentMonthFrom === tournamentMonthTo;
  const titleDate = sameMonth
    ? `${tournamentMonthFrom} ${tournamentFrom} - ${tournamentTo}`
    : `${tournamentMonthFrom} ${tournamentFrom} - ${tournamentMonthTo} ${tournamentTo}`;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-col lg:flex-row lg:gap-4 lg:items-center min-w-0">
        <span className="text-sm lg:text-lg truncate">{tournament.name}</span>
        <span className="text-xs lg:text-base text-muted-foreground capitalize">{titleDate}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <TournamentDetailsModal tournament={tournament} />
        <StatusBadge status={tournament.status ?? 'PENDING'} />
      </div>
    </div>
  );
}

function Tournament() {
  const { tournament } = useLoaderData<typeof loader>();
  return (
    <>
      <Portal id="portal-footer">
        <TournamentFooter tournament={tournament} />
      </Portal>
      <Outlet />
    </>
  );
}

const tournamentStatusToUrl = (status: Tournament['status']) => {
  return status.toLowerCase().replace(/_/g, '-');
};

const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const segments = url.pathname.split('/').filter(Boolean);
  const last = segments.at(-1);
  const isAtTournamentRoot = last === String(params.tournamentId);
  const statusFromUrl = isAtTournamentRoot ? null : last;
  const clubId = params.id!;
  const tournamentId = params.tournamentId!;
  const tournament = await clubApi.getTournamentById(clubId, tournamentId);
  if (
    isAtTournamentRoot ||
    tournamentStatusToUrl(tournament.status) !== statusFromUrl?.toLowerCase()
  ) {
    const url = urlTournament(
      clubId.toString(),
      tournamentId.toString(),
      tournamentStatusToUrl(tournament.status)
    );
    return redirect(url);
  }
  return { tournament };
};

export const tournamentRouteId = 'tournamentLayout';

export const route = {
  id: tournamentRouteId,
  loader,
  element: <Tournament />,
  children: [
    pendingRoute,
    openInscriptionsRoute,
    activeRoute,
    finishedRoute,
    configurePhasesRoute,
    {
      path: ':status',
      loader: ({ params }: LoaderFunctionArgs) =>
        redirect(urlTournament(params.id as string, params.tournamentId as string)),
    },
  ],
  path: urlTournament(':id', ':tournamentId'),
  handle: {
    title: (data: { tournament: Tournament }) => <Header tournament={data.tournament} />,
  },
};
